import type { UserAvatarPresignedUploadResponse } from "@nlc/shared-types";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { toast } from "@/components/toast/toast-queue";
import { profileKeys, usersKeys } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

interface UseAvatarUploadReturn {
  cropImageSrc: null | string;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleChangePhoto: () => void;
  handleCropApply: (blob: Blob) => void;
  handleCropClose: () => void;
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemove: () => void;
  isUploading: boolean;
}

export function useAvatarUpload(userId?: string): UseAvatarUploadReturn {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropImageSrc, setCropImageSrc] = useState<null | string>(null);

  const invalidateQueries = useCallback(() => {
    if (userId) {
      queryClient.invalidateQueries({
        queryKey: profileKeys.userById(userId),
      });
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
    } else {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    }
  }, [queryClient, userId]);

  const { isPending, mutate: updateAvatar } = useMutation({
    mutationFn: (avatarKey: null | string) => {
      const url = userId ? `/users/${userId}` : "/users/me";

      return apiClient.url(url).put({ avatar: avatarKey }).res();
    },
    onError: () => {
      toast.add({
        description: "Failed to update avatar",
        timeout: 3000,
        type: "error",
      });
    },
    onSuccess: () => {
      invalidateQueries();
    },
  });

  const handleChangePhoto = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];

      if (!file) return;

      const objectUrl = URL.createObjectURL(file);

      setCropImageSrc(objectUrl);

      // Reset so selecting the same file again triggers onChange
      e.target.value = "";
    },
    [],
  );

  const handleCropClose = useCallback(() => {
    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    setCropImageSrc(null);
  }, [cropImageSrc]);

  const handleCropApply = useCallback(
    async (blob: Blob) => {
      handleCropClose();

      try {
        const fileName = `avatar.png`;

        const presignedData = await apiClient
          .query({ contentType: "image/png", fileName })
          .get("/users/presigned-upload")
          .json<UserAvatarPresignedUploadResponse>();

        const formData = new FormData();

        for (const [key, val] of Object.entries(presignedData.fields)) {
          formData.append(key, val);
        }
        formData.append("file", blob, fileName);

        const uploadResponse = await fetch(presignedData.uploadUrl, {
          body: formData,
          method: "POST",
        });

        if (!uploadResponse.ok) {
          throw new Error("Upload failed");
        }

        updateAvatar(presignedData.s3Key);
      } catch {
        toast.add({
          description: "Failed to upload photo",
          timeout: 3000,
          type: "error",
        });
      }
    },
    [handleCropClose, updateAvatar],
  );

  const handleRemove = useCallback(() => {
    updateAvatar(null);
  }, [updateAvatar]);

  return {
    cropImageSrc,
    fileInputRef: fileInputRef as React.RefObject<HTMLInputElement>,
    handleChangePhoto,
    handleCropApply,
    handleCropClose,
    handleFileSelect,
    handleRemove,
    isUploading: isPending,
  };
}

export { type UseAvatarUploadReturn };
